import { fg } from '@atlaskit/platform-feature-flags';

import type { ForgeUIRuntimeType } from '../analytics/ForgeUIRuntimeAnalyticsContext';
import { isAppUsingWorkerRuntime } from '../worker-runtime';

/**
 * Derives the Forge rendering runtime from the extension + feature gates, mirroring the
 * branch decisions made downstream in {@link WebRuntime} (worker vs iframe) and
 * {@link Iframe} (iframe v1 vs v2).
 *
 * This is used by `ForgeUIRenderer` to tag analytics events that are fired *above* the terminal
 * runtime components — e.g. `forge.ui.renderer` (from `RendererNext`) and `forge.ui.extension`
 * `loaded` (from `CustomUIRenderer` / `Iframe`). Those events are created by hooks that run before
 * (and in sibling branches to) the terminal runtime mounts, so the runtime can only be *derived*
 * at render time; it cannot be reported up from the component that actually rendered.
 *
 * The terminal components (`WorkerWebRuntime`, `InnerIframe` v1/v2) additionally tag their own
 * events with the ground-truth literal, which wins over this derived value via the deepest-node
 * merge in `ForgeUIAnalyticsListener` — so high-volume bridge/app events stay correct even if this
 * derivation drifts from the real gate logic.
 *
 * Keep the conditions in sync with `WebRuntime` and `Iframe`.
 */
export const getForgeRuntimeType = (
	extension: Parameters<typeof isAppUsingWorkerRuntime>[0],
): ForgeUIRuntimeType => {
	if (isAppUsingWorkerRuntime(extension) && fg('forge-ui-worker-runtime')) {
		return 'webworker';
	}
	if (fg('forge-ui-bridge-core-reset')) {
		return 'iframev2';
	}
	return 'iframe';
};
